import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronLeft, Link2, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { flightApi, type FlightSourceLinkReview } from '@/services/api';

const sourceLabel: Record<FlightSourceLinkReview['source']['type'], string> = {
  eflight: 'E-Flight',
  fsop: 'FSOP',
};

function operationLabel(operation: FlightSourceLinkReview['operation']) {
  const route = [operation.departure, operation.arrival].filter(Boolean).join(' → ');
  return [operation.flightDate, operation.flightNo, route].filter(Boolean).join(' · ') || `飞行主记录 #${operation.id}`;
}

export function FlightSourceLinkReviewPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [links, setLinks] = useState<FlightSourceLinkReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<number | null>(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await flightApi.getSuggestedSourceLinks();
      setLinks(response.data.data);
    } catch {
      toast({ title: '获取待确认匹配失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadLinks();
  }, [loadLinks]);

  const review = async (link: FlightSourceLinkReview, decision: 'confirm' | 'reject') => {
    setProcessingId(link.id);
    try {
      const response = decision === 'confirm'
        ? await flightApi.confirmSourceLink(link.id)
        : await flightApi.rejectSourceLink(link.id);
      setLinks((current) => current.filter((item) => {
        if (item.id === link.id) return false;
        return decision !== 'confirm' || item.source.id !== link.source.id;
      }));
      toast({ title: response.data.message });
    } catch (error: any) {
      toast({
        title: error?.response?.data?.message || '操作失败，请稍后重试',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">待确认来源匹配</h1>
          <p className="mt-1 text-sm text-gray-500">仅确认你确定属于同一次飞行的外部记录。</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/flights')}>
          <ChevronLeft className="mr-1 h-4 w-4" />
          返回航班
        </Button>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : links.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-white p-10 text-center">
          <Link2 className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-3 font-medium text-gray-700">没有待确认的来源匹配</p>
          <p className="mt-1 text-sm text-gray-500">后续发现低置信度的 E-Flight 或 FSOP 记录时，会显示在这里。</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <section key={link.id} className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">{sourceLabel[link.source.type]} 记录 {link.source.externalId}</p>
                  <h2 className="mt-1 text-base font-semibold text-gray-900">可能对应：{operationLabel(link.operation)}</h2>
                </div>
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                  匹配度 {Math.round(link.matchScore * 100)}%
                </span>
              </div>
              {Object.keys(link.matchDetails).length > 0 && (
                <p className="mt-3 text-xs text-gray-500">匹配依据：{Object.entries(link.matchDetails).filter(([, matched]) => Boolean(matched)).map(([key]) => key).join('、') || '系统候选匹配'}</p>
              )}
              <div className="mt-4 flex gap-2">
                <Button disabled={processingId === link.id} onClick={() => void review(link, 'confirm')}>
                  {processingId === link.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                  确认是这次飞行
                </Button>
                <Button disabled={processingId === link.id} onClick={() => void review(link, 'reject')} variant="outline">
                  <X className="mr-1 h-4 w-4" />
                  不是这次飞行
                </Button>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
